<script lang="ts" setup>
    import { computed, onBeforeUnmount, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'

    const props = defineProps<{
        error?: Error
    }>()

    const emit = defineEmits<{
        (e: 'retry'): void
        (e: 'reset'): void
    }>()

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    const isContextWindowError = computed(() => {
        const errorMsg = props.error?.message?.toLowerCase() || ''
        return errorMsg.includes('context_length_exceeded')
    })

    const isNotFoundError = computed(() => {
        const errorMsg = props.error?.message?.toLowerCase() || ''
        return errorMsg.includes('entity-not-found')
    })

    const isRateLimitError = computed(() => {
        const errorMsg = props.error?.message?.toLowerCase() || ''
        return errorMsg.includes('rate-limit-exceeded')
    })

    const isOriginNotAllowedError = computed(() => {
        const errorMsg = props.error?.message?.toLowerCase() || ''
        return errorMsg.includes('origin-not-allowed')
    })

    const isRefreshableError = computed(() => {
        return (
            !isContextWindowError.value &&
            !isNotFoundError.value &&
            !isOriginNotAllowedError.value
        )
    })

    const errorMessage = computed(() => {
        if (isContextWindowError.value) {
            return $t('message.chatErrorContextWindow')
        }
        if (isNotFoundError.value) {
            return $t('message.chatErrorNotFound')
        }
        if (isRateLimitError.value) {
            return $t('message.chatErrorRateLimit', {
                seconds: retryCountdown.value,
            })
        }
        if (isOriginNotAllowedError.value) {
            return $t('message.chatErrorOriginNotAllowed')
        }
        return $t('message.chatErrorGeneric', { seconds: retryCountdown.value })
    })

    const retryCountdown = ref<number>(0)
    const retryTimer = ref<NodeJS.Timeout>()

    watch(
        () => props.error,
        (error) => {
            if (retryTimer.value) {
                clearInterval(retryTimer.value)
            }

            if (error && isRefreshableError.value) {
                retryCountdown.value = 10
                retryTimer.value = setInterval(() => {
                    retryCountdown.value--
                    if (retryCountdown.value <= 0) {
                        clearInterval(retryTimer.value)
                        emit('retry')
                    }
                }, 1000)
            } else {
                retryCountdown.value = 0
            }
        },
        { immediate: true },
    )

    onBeforeUnmount(() => {
        if (retryTimer.value) {
            clearInterval(retryTimer.value)
        }
    })
</script>

<template>
    <div class="flex">
        <VvAlert
            :modifiers="isRefreshableError ? 'warning' : 'danger'"
            class="rounded-lg">
            <div
                class="flex gap-sm items-center"
                :class="
                    isRefreshableError
                        ? 'text-warning-darken-4'
                        : 'text-danger-darken-2'
                ">
                <VvIcon
                    :name="
                        isRefreshableError
                            ? 'ri:alert-line'
                            : 'ri:error-warning-line'
                    "
                    class="text-48" />
                {{ errorMessage }}
            </div>
            <template #footer>
                <VvButton
                    v-if="isRefreshableError"
                    icon="ri:reset-right-line"
                    modifiers="action"
                    class="text-12"
                    :label="$t('action.retryNow')"
                    @click="$emit('retry')" />
                <VvButton
                    v-else-if="!isOriginNotAllowedError"
                    icon="reload"
                    modifiers="action"
                    :label="$t('label.newChat')"
                    @click="$emit('reset')" />
            </template>
        </VvAlert>
    </div>
</template>
